import type { EquityStatsResponse, PnlStatsResponse } from '@et/shared';
import { useState } from 'react';
import { EquityChart, PnlBars } from '../components/Charts';
import { RejectionsCard } from '../components/Insights';
import { Feed, PositionList } from '../components/Tables';
import { duration, money, signed, tone } from '../lib/format';
import { useResource } from '../lib/use-resource';
import { useLive } from '../stores/live';
import { useT } from '../lib/i18n';

const RANGES = ['7d', '30d', '90d', 'all'] as const;

export function Dashboard(): JSX.Element {
  const { status, positions, pnl, feed } = useLive();
  const t = useT();
  const [range, setRange] = useState<(typeof RANGES)[number]>('30d');
  const equity = useResource<EquityStatsResponse>(`/stats/equity?range=${range}`);
  const stats = useResource<PnlStatsResponse>(`/stats/pnl?range=${range}`);

  return (
    <div className="stack">
      <div className="grid tiles">
        <div className="card tile"><div className="label">{t('Equity (USDT)')}</div><div className="value">{money(pnl?.equity)}</div></div>
        <div className="card tile"><div className="label">{t('Today')}</div><div className={`value ${tone(pnl?.dailyPnl)}`}>{signed(pnl?.dailyPnl)}</div></div>
        <div className="card tile">
          <div className="label">{t('Drawdown today / limit')}</div>
          <div className="value">{money(status?.circuitBreaker.drawdownPct)}% <span className="muted" style={{ fontSize: 14 }}>/ {status?.circuitBreaker.limitPct}%</span></div>
          {status?.circuitBreaker.tripped && <span className="chip critical" style={{ marginTop: 6 }}>{t('Breaker tripped — entries blocked')}</span>}
        </div>
        <div className="card tile"><div className="label">{t('Strategy')}</div><div className="value" style={{ fontSize: 16 }}>{status && status.activeStrategies.length > 0 ? status.activeStrategies.map((k) => t(k)).join(' + ') : t('none active')}</div></div>
        <div className="card tile"><div className="label">{t('Uptime')}</div><div className="value" style={{ fontSize: 16 }}>{status ? duration(status.uptimeSec) : '—'}</div></div>
      </div>

      <div className="row" role="group" aria-label={t('Time range')}>
        {RANGES.map((r) => <button key={r} className={`small ${r === range ? 'primary' : ''}`} onClick={() => { setRange(r); }}>{t(r)}</button>)}
      </div>

      <div className="grid cols-2">
        <div className="card">
          <h2>{t('Equity')}</h2>
          {equity.error ? <div className="neg">{equity.error}</div> : <EquityChart points={equity.data?.points ?? []} />}
        </div>
        <div className="card">
          <h2>{t('Realized PnL per day')}</h2>
          <PnlBars days={stats.data?.daily.filter((d) => d.trades > 0) ?? []} />
          {stats.data && (
            <div className="row muted" style={{ marginTop: 8, fontSize: 12, gap: 16 }}>
              <span>{t('Total')} <b className={tone(stats.data.totalRealized)}>{signed(stats.data.totalRealized)}</b></span>
              <span>{t('Win rate')} <b>{money((Number.parseFloat(stats.data.winRate) * 100).toFixed(1), 1)}%</b></span>
              <span>{t('Profit factor')} <b>{stats.data.profitFactor ?? '—'}</b></span>
              <span>{t('Max DD')} <b>{stats.data.maxDrawdownPct}%</b></span>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <h2>{t('Open positions ({n})', { n: positions.length })}</h2>
        <PositionList positions={positions} onChanged={() => void useLive.getState().loadAll()} />
      </div>

      <RejectionsCard />

      <div className="card">
        <h2>{t('Live feed')}</h2>
        <Feed entries={feed} />
      </div>
    </div>
  );
}
